import React, { useState, useEffect, useRef } from "react";
import {
  Form,
  FormGroup,
  FormLabel,
  FormControl,
  Button,
  Accordion,
} from "react-bootstrap";
import { BsTrash } from "react-icons/bs";
import { DiagramacaoFormProps } from "./types/types";
import useInitialImages from "../../hooks/useImageProcessing";
import "./DiagramacaoForm.css";
import { STORAGE_URL } from "../../../../../constants/consts";
import { useNavigate } from "react-router-dom";
import { validateField, validateForm } from "./validations/validations";
import { Attention } from "../../../../../component/base/Attention";

interface Errors {
  [key: string]: string;
}

export default function DiagramacaoForm(props: DiagramacaoFormProps) {
  const {
    editMode, // indica se está em modo de edição
    nameTestSelect,
    provas,
    provaSelect,
    nomeProva,
    setProvaSelect,
    setNomeProva,
    setNumeroColunas,
    fontSizeQuestoes,
    setFontSizeQuestoes,
    marginSuperior,
    setMarginSuperior,
    marginInferior,
    setMarginInferior,
    marginEsquerda,
    setMarginEsquerda,
    marginDireita,
    setMarginDireita,
    handleSave,
    loading,
    setLoading,
    msgErro,
    setMsgErro,
    getTestById,
    codeExam,
    setCodeExam,
    title,
    setTitle,
    subtitle,
    setSubtitle,
    titleHeader,
    subtitleHeader,
    logo,
    setLogo,
    logoBottom,
    setLogoBottom,
    fontSizeRules,
    setFontSizeRules,
    fontSizeAlternatives,
    setFontSizeAlternatives,
    showDiscipline,
    setShowDiscipline,
    borderVisible,
    setBorderVisible,
    randomQuestion,
    setRandomQuestion,
    randomAlternatives,
    setRandomAlternatives,
    defaultLayout,
    setDefaultLayout,
    nameDefault,
    setNameDefault,
    setDataFrontPage,
  } = props;

  const navigate = useNavigate();
  const [errors, setErrors] = useState<Errors>({});

  // Refs para inputs de arquivos
  const inputSuperiorRef = useRef<HTMLInputElement | null>(null);
  const inputInferiorRef = useRef<HTMLInputElement | null>(null);

  // Hook para processamento de imagens
  const { processImages } = useInitialImages();

  // Handler para selecionar a prova via select
  const handleSelectProva = async (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    if (editMode) {
      return;
    }
    const selectedId = event.target.value;
    if (!selectedId) {
      setProvaSelect(null);
      setNomeProva("");
      setTitle("");
      setSubtitle("");
      setDataFrontPage((prevData) =>
        prevData.map((item) => ({
          ...item,
          element: item.baseElement ?? item.element,
        }))
      );
      setErrors((prev) => ({ ...prev, provaSelect: "Selecione uma prova." }));
      return;
    }
    setErrors((prev) => ({ ...prev, provaSelect: "" }));
    setLoading(true);
    try {
      const provaDetalhes = await getTestById(selectedId);
      if (!provaDetalhes) {
        throw new Error("Não foi possível carregar os detalhes da prova");
      }
      const provaComImagensProcessadas = await processImages(provaDetalhes);
      setProvaSelect(provaComImagensProcessadas);

      const novoNomeProva = provaComImagensProcessadas?.name || "";
      const novoTitle = provaComImagensProcessadas?.organization?.name || "";
      const novoSubtitle = provaComImagensProcessadas?.name || "";
      setNomeProva(novoNomeProva);
      setTitle(novoTitle);
      setSubtitle(novoSubtitle);

      const organizationLogoUrl = provaComImagensProcessadas?.organization?.logo
        ? encodeURI(
            `${STORAGE_URL}${provaComImagensProcessadas.organization.logo}`
          )
        : "";
      if (!logo) {
        setLogo(organizationLogoUrl);
      }
      if (!logoBottom) {
        setLogoBottom(organizationLogoUrl);
      }
      setDataFrontPage((prevData) =>
        prevData.map((item) => ({
          ...item,
          baseElement: item.baseElement ?? item.element,
        }))
      );
    } catch (error) {
      console.error("Erro ao buscar prova:", error);
      setMsgErro("Erro ao buscar detalhes da prova");
    } finally {
      setLoading(false);
    }
  };

  // Atualiza os elementos do DataFrontPage conforme valores mudam
  useEffect(() => {
    setDataFrontPage((prevData) =>
      prevData.map((item) => {
        const originalHtml = item.baseElement ?? item.element;
        let replaced = originalHtml;
        replaced = replaced.replace(/:title/g, title || "");
        replaced = replaced.replace(/:subtitle/g, subtitle || "");
        replaced = replaced.replace(/:header_title/g, titleHeader || "");
        replaced = replaced.replace(/:header_subtitle/g, subtitleHeader || "");

        if (replaced.includes("%LOGO%")) {
          replaced = replaced.replace(
            /%LOGO%/g,
            typeof logo === "string" ? logo : ""
          );
        }
        if (replaced.includes("%LOGO_BOTTOM%")) {
          replaced = replaced.replace(
            /%LOGO_BOTTOM%/g,
            typeof logoBottom === "string" ? logoBottom : ""
          );
        }
        replaced = replaced.replace(/:nome_cargo/g, nomeProva || "");
        replaced = replaced.replace(/:nome_seletivo/g, title || "");
        replaced = replaced.replace(
          /:nome_prova/g,
          nomeProva || "Nome da prova"
        );

        return {
          ...item,
          element: replaced,
          baseElement: item.baseElement ?? item.element,
        };
      })
    );
  }, [
    title,
    subtitle,
    titleHeader,
    subtitleHeader,
    logo,
    logoBottom,
    nomeProva,
    setDataFrontPage,
  ]);

  // Se a prova for do tipo dissertativa, força 1 coluna
  useEffect(() => {
    if (provaSelect?.type === "dissertative") {
      setNumeroColunas(1);
    }
  }, [provaSelect, setNumeroColunas]);

  // Validações em tempo real para campos de texto
  const handleCodeExamChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCodeExam(value);
    const error = validateField("codeExam", value);
    setErrors((prev) => ({ ...prev, codeExam: error }));
  };

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTitle(value);
    const error = validateField("title", value);
    setErrors((prev) => ({ ...prev, title: error }));
  };

  const handleSubtitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSubtitle(value);
    const error = validateField("subtitle", value);
    setErrors((prev) => ({ ...prev, subtitle: error }));
  };

  const handleNameDefaultChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNameDefault(value);
    const error = validateField("nameDefault", value, { defaultLayout });
    setErrors((prev) => ({ ...prev, nameDefault: error }));
  };

  // Ao submeter o formulário, valida todos os campos
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formErrors = validateForm({
      provaSelect,
      codeExam,
      title,
      subtitle,
      defaultLayout,
      nameDefault,
    });
    setErrors(formErrors);
    const hasErrors = Object.values(formErrors).some((error) => error);
    if (hasErrors) {
      return;
    }
    handleSave(false);
  };

  return (
    <div className="hidethis" data-testid="diagramacao-form">
      <h4>Configurações de Diagramação</h4>
      {msgErro && <div className="alert alert-danger" data-testid="erro-message">{msgErro}</div>}
      <Form onSubmit={handleSubmit} data-testid="form-diagramacao">
        <Accordion defaultActiveKey="0" className="mb-4" data-testid="accordion-config">
          {/* Informações Básicas */}
          <Accordion.Item eventKey="0">
            <Accordion.Header>Informações Básicas</Accordion.Header>
            <Accordion.Body>
              <FormGroup className="mb-3">
                <FormLabel>
                  Selecione uma prova <Attention />{" "}
                </FormLabel>
                <FormControl
                  as="select"
                  value={provaSelect?.id ?? ""}
                  onChange={(e) =>
                    handleSelectProva(
                      e as unknown as React.ChangeEvent<HTMLSelectElement>
                    )
                  }
                  disabled={editMode}
                  data-testid="select-prova"
                >
                  {editMode ? (
                    <option value={provaSelect?.id}>
                      {nameTestSelect || "erro"}
                    </option>
                  ) : (
                    <>
                      <option value="">Selecione...</option>
                      {provas.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </>
                  )}
                </FormControl>
                {errors.provaSelect && (
                  <div className="error-message" style={{ color: "red" }} data-testid="error-prova-select">
                    {errors.provaSelect}
                  </div>
                )}
              </FormGroup>
              <FormGroup className="mb-3">
                <FormLabel>
                  Código da Prova <Attention />
                </FormLabel>
                <FormControl
                  type="text"
                  value={codeExam}
                  onChange={handleCodeExamChange}
                  placeholder="Ex: Prova A"
                  data-testid="input-code-exam"
                />
                {errors.codeExam && (
                  <div className="error-message" style={{ color: "red" }} data-testid="error-code-exam">
                    {errors.codeExam}
                  </div>
                )}
              </FormGroup>
              <FormGroup className="mb-3">
                <FormLabel>
                  Título (Instituição, por exemplo) <Attention />
                </FormLabel>
                <FormControl
                  type="text"
                  value={title}
                  onChange={handleTitleChange}
                  placeholder="Ex: Nome da Instituição"
                  data-testid="input-title"
                />
                {errors.title && (
                  <div className="error-message" style={{ color: "red" }} data-testid="error-title">
                    {errors.title}
                  </div>
                )}
              </FormGroup>
              <FormGroup className="mb-3">
                <FormLabel>
                  Subtítulo (Local, por exemplo) <Attention />
                </FormLabel>
                <FormControl
                  type="text"
                  value={subtitle}
                  onChange={handleSubtitleChange}
                  placeholder="Ex: Local da Prova"
                  data-testid="input-subtitle"
                />
                {errors.subtitle && (
                  <div className="error-message" style={{ color: "red" }} data-testid="error-subtitle">
                    {errors.subtitle}
                  </div>
                )}
              </FormGroup>
            </Accordion.Body>
          </Accordion.Item>

          {/* Configurações de Layout */}
          <Accordion.Item eventKey="1">
            <Accordion.Header>Configurações de Layout</Accordion.Header>
            <Accordion.Body>
              <FormGroup className="mb-3">
                <FormLabel>Número de colunas</FormLabel>
                <FormControl
                  as="select"
                  value={1}
                  onChange={(e) => setNumeroColunas(Number(e.target.value))}
                  disabled={provaSelect?.type === "dissertative"}
                  data-testid="select-numero-colunas"
                >
                  <option value={1}>1 coluna</option>
                  <option value={2}>2 colunas</option>
                </FormControl>
              </FormGroup>
              <div className="row">
                <div className="col-4">
                  <FormGroup className="mb-3">
                    <FormLabel>Tamanho da fonte das regras</FormLabel>
                    <FormControl
                      as="select"
                      value={fontSizeRules}
                      onChange={(e) => setFontSizeRules(Number(e.target.value))}
                      data-testid="select-font-rules"
                    >
                      {Array.from({ length: 13 }, (_, i) => 9 + i).map(
                        (val) => (
                          <option key={val} value={val}>
                            {val}px
                          </option>
                        )
                      )}
                    </FormControl>
                  </FormGroup>
                </div>
                <div className="col-4">
                  <FormGroup className="mb-3">
                    <FormLabel>Tamanho da fonte das questões</FormLabel>
                    <FormControl
                      as="select"
                      value={fontSizeQuestoes}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "default") {
                          setFontSizeQuestoes("default");
                        } else {
                          setFontSizeQuestoes(Number(val));
                        }
                      }}
                      data-testid="select-font-questoes"
                    >
                      <option value="default">Padrão do cadastro</option>
                      {Array.from({ length: 13 }, (_, i) => 9 + i).map(
                        (val) => (
                          <option key={val} value={val}>
                            {val}px
                          </option>
                        )
                      )}
                    </FormControl>
                  </FormGroup>
                </div>
                <div className="col-4">
                  <FormGroup className="mb-3">
                    <FormLabel>Tamanho da fonte das alternativas</FormLabel>
                    <FormControl
                      as="select"
                      value={fontSizeAlternatives}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "default") {
                          setFontSizeAlternatives("default");
                        } else {
                          setFontSizeAlternatives(Number(val));
                        }
                      }}
                      data-testid="select-font-alternatives"
                    >
                      <option value="default">Padrão do cadastro</option>
                      {Array.from({ length: 13 }, (_, i) => 9 + i).map(
                        (val) => (
                          <option key={val} value={val}>
                            {val}px
                          </option>
                        )
                      )}
                    </FormControl>
                  </FormGroup>
                </div>
              </div>
            </Accordion.Body>
          </Accordion.Item>

          {/* Configurações de Margem */}
          <Accordion.Item eventKey="2">
            <Accordion.Header>Configurações de Margem</Accordion.Header>
            <Accordion.Body>
              <div className="row">
                <div className="col-6 col-md-3">
                  <FormGroup className="mb-3">
                    <FormLabel>Margem Superior</FormLabel>
                    <FormControl
                      as="select"
                      value={marginSuperior}
                      onChange={(e) =>
                        setMarginSuperior(Number(e.target.value))
                      }
                    >
                      {Array.from({ length: 81 }, (_, i) => i).map((v) => (
                        <option key={v} value={v}>
                          {v}px
                        </option>
                      ))}
                    </FormControl>
                  </FormGroup>
                </div>
                <div className="col-6 col-md-3">
                  <FormGroup className="mb-3">
                    <FormLabel>Margem Inferior</FormLabel>
                    <FormControl
                      as="select"
                      value={marginInferior}
                      onChange={(e) =>
                        setMarginInferior(Number(e.target.value))
                      }
                    >
                      {Array.from({ length: 81 }, (_, i) => i).map((v) => (
                        <option key={v} value={v}>
                          {v}px
                        </option>
                      ))}
                    </FormControl>
                  </FormGroup>
                </div>
                <div className="col-6 col-md-3">
                  <FormGroup className="mb-3">
                    <FormLabel>Margem Esquerda</FormLabel>
                    <FormControl
                      as="select"
                      value={marginEsquerda}
                      onChange={(e) =>
                        setMarginEsquerda(Number(e.target.value))
                      }
                    >
                      {Array.from({ length: 81 }, (_, i) => i).map((v) => (
                        <option key={v} value={v}>
                          {v}px
                        </option>
                      ))}
                    </FormControl>
                  </FormGroup>
                </div>
                <div className="col-6 col-md-3">
                  <FormGroup className="mb-3">
                    <FormLabel>Margem Direita</FormLabel>
                    <FormControl
                      as="select"
                      value={marginDireita}
                      onChange={(e) => setMarginDireita(Number(e.target.value))}
                    >
                      {Array.from({ length: 81 }, (_, i) => i).map((v) => (
                        <option key={v} value={v}>
                          {v}px
                        </option>
                      ))}
                    </FormControl>
                  </FormGroup>
                </div>
              </div>
            </Accordion.Body>
          </Accordion.Item>

          {/* Configurações de Randomização */}
          <Accordion.Item eventKey="3">
            <Accordion.Header>Configurações de Randomização</Accordion.Header>
            <Accordion.Body>
              <div className="row">
                <div className="col-6">
                  <FormGroup className="mb-3">
                    <FormLabel>Ordem das Questões</FormLabel>
                    <FormControl
                      as="select"
                      value={randomQuestion ? "true" : "false"}
                      onChange={(e) =>
                        setRandomQuestion(e.target.value === "true")
                      }
                    >
                      <option value="false">Ordem de cadastro</option>
                      <option value="true">Ordem aleatória</option>
                    </FormControl>
                  </FormGroup>
                </div>
                <div className="col-6">
                  <FormGroup className="mb-3">
                    <FormLabel>Ordem das Alternativas</FormLabel>
                    <FormControl
                      as="select"
                      value={randomAlternatives ? "true" : "false"}
                      onChange={(e) =>
                        setRandomAlternatives(e.target.value === "true")
                      }
                    >
                      <option value="false">Ordem de cadastro</option>
                      <option value="true">Ordem aleatória</option>
                    </FormControl>
                  </FormGroup>
                </div>
              </div>
            </Accordion.Body>
          </Accordion.Item>

          {/* Outras Configurações */}
          <Accordion.Item eventKey="5">
            <Accordion.Header>Outras Configurações</Accordion.Header>
            <Accordion.Body>
              <div className="row">
                <div className="col-6">
                  <FormGroup className="mb-3">
                    <FormLabel>Mostrar Disciplinas</FormLabel>
                    <FormControl
                      as="select"
                      value={showDiscipline ? "true" : "false"}
                      onChange={(e) =>
                        setShowDiscipline(e.target.value === "true")
                      }
                    >
                      <option value="true">Sim</option>
                      <option value="false">Não</option>
                    </FormControl>
                  </FormGroup>
                </div>
                <div className="col-6">
                  <FormGroup className="mb-3">
                    <FormLabel>Borda Visível</FormLabel>
                    <FormControl
                      as="select"
                      value={borderVisible ? "true" : "false"}
                      onChange={(e) =>
                        setBorderVisible(e.target.value === "true")
                      }
                    >
                      <option value="true">Sim</option>
                      <option value="false">Não</option>
                    </FormControl>
                  </FormGroup>
                </div>
              </div>
            </Accordion.Body>
          </Accordion.Item>

          {/* Logos */}
          <Accordion.Item eventKey="8">
            <Accordion.Header>Logos</Accordion.Header>
            <Accordion.Body>
              <div className="row">
                <div className="col-6">
                  <FormGroup className="mb-3">
                    <FormLabel>Logo Superior</FormLabel>
                    <div className="d-flex flex-column gap-3 align-items-center">
                      <FormControl
                        type="file"
                        ref={inputSuperiorRef}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const file =
                            e.target.files && e.target.files.length > 0
                              ? e.target.files[0]
                              : null;
                          if (file) {
                            const objectUrl = URL.createObjectURL(file);
                            setLogo(objectUrl);
                          }
                        }}
                        accept="image/*"
                      />
                      {logo && (
                        <div className="preview-container">
                          <img
                            src={
                              typeof logo === "string"
                                ? logo
                                : URL.createObjectURL(logo)
                            }
                            alt="Logo preview"
                            style={{
                              maxWidth: "150px",
                              height: "auto",
                              border: "2px solid #ccc",
                              borderRadius: "10px",
                              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                            }}
                          />
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => {
                              setLogo(null);
                              if (inputSuperiorRef.current) {
                                inputSuperiorRef.current.value = "";
                              }
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "5px",
                            }}
                          >
                            <BsTrash /> Remover
                          </Button>
                        </div>
                      )}
                    </div>
                  </FormGroup>
                </div>
                <div className="col-6">
                  <FormGroup className="mb-3">
                    <FormLabel>Logo Inferior</FormLabel>
                    <div className="d-flex flex-column gap-3 align-items-center">
                      <FormControl
                        type="file"
                        ref={inputInferiorRef}
                        onChange={(e) => {
                          const files = (e.target as HTMLInputElement).files;
                          const file =
                            files && files.length > 0 ? files[0] : null;
                          if (file) {
                            const objectUrl = URL.createObjectURL(file);
                            setLogoBottom(objectUrl);
                          }
                        }}
                        accept="image/*"
                      />
                      {logoBottom && (
                        <div className="preview-container">
                          <img
                            src={
                              typeof logoBottom === "string"
                                ? logoBottom
                                : URL.createObjectURL(logoBottom)
                            }
                            alt="Logo preview"
                            style={{
                              maxWidth: "150px",
                              height: "auto",
                              border: "2px solid #ccc",
                              borderRadius: "10px",
                              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                            }}
                          />
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => {
                              setLogoBottom(null);
                              if (inputInferiorRef.current) {
                                inputInferiorRef.current.value = "";
                              }
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "5px",
                            }}
                          >
                            <BsTrash /> Remover
                          </Button>
                        </div>
                      )}
                    </div>
                  </FormGroup>
                </div>
              </div>
            </Accordion.Body>
          </Accordion.Item>

          {/* Configurações de Layout Padrão */}
          <Accordion.Item eventKey="9">
            <Accordion.Header>Configurações de Layout Padrão</Accordion.Header>
            <Accordion.Body>
              <FormGroup className="mb-3">
                <FormLabel>Salvar como Layout Padrão</FormLabel>
                <FormControl
                  as="select"
                  value={defaultLayout ? "true" : "false"}
                  onChange={(e) => setDefaultLayout(e.target.value === "true")}
                >
                  <option value="false">Não</option>
                  <option value="true">Sim</option>
                </FormControl>
              </FormGroup>
              {defaultLayout && (
                <FormGroup className="mb-3">
                  <FormLabel>Nome do Layout Padrão</FormLabel>
                  <FormControl
                    type="text"
                    value={nameDefault}
                    onChange={handleNameDefaultChange}
                    placeholder="Ex: Layout Padrão 1"
                  />
                  {errors.nameDefault && (
                    <div className="error-message">{errors.nameDefault}</div>
                  )}
                </FormGroup>
              )}
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>

        <div className="d-flex justify-content-between mt-4">
          <Button
            variant="secondary"
            onClick={() => navigate("/layouts_default_exams")}
            data-testid="button-voltar"
          >
            Voltar
          </Button>
          <div>
            <Button
              variant="info"
              disabled={loading}
              onClick={() => handleSave(true)}
              className="me-2"
              data-testid="button-save-print"
            >
              {loading ? "Carregando..." : "Salvar & Imprimir"}
            </Button>
            <Button 
              variant="primary" 
              type="submit" 
              disabled={loading}
              data-testid="button-save"
            >
              {loading ? "Carregando..." : "Salvar"}
            </Button>
          </div>
        </div>
      </Form>
    </div>
  );
}
